(function () {
  'use strict';

  const COMMIT_THRESHOLD = 0.5;
  const SHADE_OPACITY = 0.4;

  function videoIdFromUrl(url) {
    if (!url) return null;
    const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
    return m ? m[1] : null;
  }

  function initSection(section) {
    if (section.dataset.customVideoSliderInitialized) return;
    section.dataset.customVideoSliderInitialized = 'true';

    const stage = section.querySelector('[data-video-stage]');
    if (!stage) return;
    const tiles = Array.from(stage.querySelectorAll('.custom-video-slider__tile'));
    if (tiles.length === 0) return;

    const n = tiles.length;
    let activeIndex = 0;
    let dragProgress = 0;
    let rafId = null;

    function unloadAllVideos() {
      tiles.forEach((tile) => {
        const frame = tile.querySelector('[data-tile-frame]');
        if (!frame) return;
        const iframe = frame.querySelector('iframe');
        if (iframe) iframe.remove();
        tile.classList.remove('is-playing');
      });
    }

    function baseSlot(i) {
      let s = i - activeIndex;
      if (s > n / 2) s -= n;
      if (s < -n / 2) s += n;
      return s;
    }

    function getCssVar(name, fallback) {
      const v = getComputedStyle(stage).getPropertyValue(name).trim();
      if (!v) return fallback;
      if (v.endsWith('px')) return parseFloat(v);
      if (v.endsWith('rem')) return parseFloat(v) * 16;
      if (v.endsWith('vw')) return (parseFloat(v) / 100) * window.innerWidth;
      return parseFloat(v) || fallback;
    }

    function syncThumbs() {
      const thumbRail = section.querySelector('[data-video-thumb-rail]');
      if (!thumbRail) return;
      thumbRail.querySelectorAll('.custom-video-slider__thumb').forEach((t, i) => {
        const active = i === activeIndex;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }

    function syncTileA11y() {
      tiles.forEach((tile, i) => {
        const active = i === activeIndex;
        tile.setAttribute('tabindex', active ? '0' : '-1');
        const playBtn = tile.querySelector('[data-play-button]');
        if (playBtn) playBtn.tabIndex = active ? 0 : -1;
      });
    }

    function render() {
      if (window.innerWidth <= 749) {
        tiles.forEach((tile, i) => {
          tile.setAttribute('data-visible', 'true');
          tile.style.transform = '';
          tile.style.zIndex = '';
          tile.style.opacity = '';
          const shade = tile.querySelector('.custom-video-slider__tile-shade');
          if (shade) shade.style.opacity = '';
          tile.classList.toggle('is-active-position', i === activeIndex);
        });
        syncThumbs();
        syncTileA11y();
        return;
      }

      const slotX = getCssVar('--slot-x', 351);
      const centerScale = getCssVar('--center-scale', 1.366);

      tiles.forEach((tile, i) => {
        const b = baseSlot(i);
        let eff;
        let isBackPath = false;

        if (n >= 3 && b === 1 && dragProgress > 0) {
          eff = 1 - 2 * dragProgress;
          isBackPath = true;
        } else if (n >= 3 && b === -1 && dragProgress < 0) {
          eff = -1 - 2 * dragProgress;
          isBackPath = true;
        } else {
          eff = b + dragProgress;
        }

        const dist = Math.abs(eff);

        if (dist > 1.5) {
          tile.setAttribute('data-visible', 'false');
          tile.classList.remove('is-active-position');
          return;
        }
        tile.setAttribute('data-visible', 'true');

        const tx = eff * slotX;
        const proximity = Math.max(0, 1 - Math.min(dist, 1));
        const backShrink = isBackPath ? Math.max(0, 1 - dist) : 0;
        const scale = 1 + (centerScale - 1) * proximity * (isBackPath ? 0 : 1) - 0.3 * backShrink;
        tile.style.transform = `translate(calc(-50% + ${tx}px), -50%) scale(${scale})`;
        tile.style.zIndex = String(isBackPath ? 1 : Math.round(7 - dist * 3));
        tile.style.opacity = '1';

        const shade = tile.querySelector('.custom-video-slider__tile-shade');
        if (shade) shade.style.opacity = String(isBackPath ? SHADE_OPACITY + (1 - SHADE_OPACITY) * backShrink : SHADE_OPACITY * (1 - proximity));

        tile.classList.toggle('is-active-position', !isBackPath && dist < 0.5);
      });
      syncTileA11y();
    }

    function scheduleRender() {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        render();
      });
    }

    function advance(direction) {
      activeIndex = (activeIndex + direction + n) % n;
      unloadAllVideos();
      render();
    }

    function commitDrag() {
      if (Math.abs(dragProgress) >= COMMIT_THRESHOLD) {
        const direction = dragProgress > 0 ? -1 : 1;
        activeIndex = (activeIndex + direction + n) % n;
        unloadAllVideos();
      }
      dragProgress = 0;
      render();
    }

    let dragStartX = null;
    let pointerId = null;

    stage.addEventListener('dragstart', (e) => e.preventDefault());

    stage.addEventListener('pointerdown', (e) => {
      if (e.target.closest('iframe')) return;
      if (window.innerWidth <= 749) return;
      dragStartX = e.clientX;
      pointerId = e.pointerId;
    });

    stage.addEventListener('pointermove', (e) => {
      if (dragStartX === null) return;
      const dx = e.clientX - dragStartX;
      if (!stage.classList.contains('is-dragging')) {
        if (Math.abs(dx) <= 4) return;
        stage.classList.add('is-dragging');
        if (pointerId !== null) {
          try { stage.setPointerCapture(pointerId); } catch (_) {}
        }
      }
      const slotX = getCssVar('--slot-x', 351);
      const raw = dx / slotX;
      dragProgress = Math.max(-1, Math.min(1, raw));
      scheduleRender();
    });

    function endDrag() {
      if (dragStartX === null) return;
      const wasDragging = stage.classList.contains('is-dragging');
      stage.classList.remove('is-dragging');
      if (pointerId !== null && stage.hasPointerCapture(pointerId)) {
        stage.releasePointerCapture(pointerId);
      }
      dragStartX = null;
      pointerId = null;
      if (wasDragging) {
        stage.dataset.suppressClick = '1';
        setTimeout(() => delete stage.dataset.suppressClick, 0);
      }
      commitDrag();
    }

    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);

    tiles.forEach((tile, i) => {
      tile.addEventListener('click', (e) => {
        if (stage.dataset.suppressClick) {
          e.preventDefault();
          return;
        }
        if (!tile.classList.contains('is-active-position')) {
          e.preventDefault();
          unloadAllVideos();
          activeIndex = i;
          dragProgress = 0;
          render();
        } else if (!tile.classList.contains('is-playing')) {
          playVideo(tile);
        }
      });

      tile.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          if (!tile.classList.contains('is-active-position')) {
            e.preventDefault();
            unloadAllVideos();
            activeIndex = i;
            dragProgress = 0;
            render();
          } else if (!tile.classList.contains('is-playing')) {
            e.preventDefault();
            playVideo(tile);
          }
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          advance(1);
          tiles[activeIndex].focus();
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault();
          advance(-1);
          tiles[activeIndex].focus();
        }
      });

      const playBtn = tile.querySelector('[data-play-button]');
      if (playBtn) {
        playBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          playVideo(tile);
        });
      }
    });

    function playVideo(tile) {
      const url = tile.getAttribute('data-video-url');
      const id = videoIdFromUrl(url);
      if (!id) return;
      const frame = tile.querySelector('[data-tile-frame]');
      if (!frame) return;
      const title = tile.getAttribute('data-video-title') || 'Video';
      const iframe = document.createElement('iframe');
      iframe.className = 'custom-video-slider__tile-iframe';
      iframe.src = 'https://www.youtube-nocookie.com/embed/' + id + '?autoplay=1&rel=0&playsinline=1&modestbranding=1';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';
      iframe.title = title + ' (YouTube)';
      frame.appendChild(iframe);
      tile.classList.add('is-playing');
    }

    const thumbRail = section.querySelector('[data-video-thumb-rail]');
    if (thumbRail) {
      const thumbs = Array.from(thumbRail.querySelectorAll('.custom-video-slider__thumb'));
      thumbs.forEach((thumb, i) => {
        thumb.addEventListener('click', () => {
          unloadAllVideos();
          activeIndex = i;
          dragProgress = 0;
          render();
        });
        thumb.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const dir = e.key === 'ArrowRight' ? 1 : -1;
            const next = thumbs[(i + dir + thumbs.length) % thumbs.length];
            next.focus();
          }
        });
      });
    }

    const prevBtn = section.querySelector('[data-video-prev]');
    const nextBtn = section.querySelector('[data-video-next]');
    [prevBtn, nextBtn].forEach((btn) => {
      if (btn) btn.addEventListener('pointerdown', (e) => e.stopPropagation());
    });
    if (prevBtn) prevBtn.addEventListener('click', () => advance(-1));
    if (nextBtn) nextBtn.addEventListener('click', () => advance(1));

    let resizeRaf = null;
    window.addEventListener('resize', () => {
      if (resizeRaf !== null) return;
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null;
        render();
      });
    });

    // Auto-load YouTube thumbnails for tiles and thumbs without custom images.
    tiles.forEach((tile, i) => {
      const frame = tile.querySelector('[data-tile-frame]');
      const placeholder = frame && frame.querySelector('.custom-video-slider__tile-image--placeholder');
      if (!placeholder) return;
      const url = tile.getAttribute('data-video-url');
      const id = videoIdFromUrl(url);
      if (!id) return;
      const img = document.createElement('img');
      img.className = 'custom-video-slider__tile-image';
      img.alt = tile.getAttribute('data-video-title') || 'Video';
      img.loading = 'lazy';
      img.draggable = false;
      const tileFallback = 'https://img.youtube.com/vi/' + id + '/hqdefault.jpg';
      img.onerror = function () {
        this.onerror = null;
        this.src = tileFallback;
      };
      img.onload = function () {
        if (this.naturalWidth <= 120) {
          this.onload = null;
          this.src = tileFallback;
        }
      };
      img.src = 'https://img.youtube.com/vi/' + id + '/maxresdefault.jpg';
      placeholder.replaceWith(img);

      const tRail = section.querySelector('[data-video-thumb-rail]');
      if (!tRail) return;
      const thumb = tRail.querySelectorAll('.custom-video-slider__thumb')[i];
      if (!thumb) return;
      const thumbPlaceholder = thumb.querySelector('.custom-video-slider__thumb-image--placeholder');
      if (!thumbPlaceholder) return;
      const thumbImg = document.createElement('img');
      thumbImg.className = 'custom-video-slider__thumb-image';
      thumbImg.alt = '';
      thumbImg.loading = 'lazy';
      thumbImg.src = 'https://img.youtube.com/vi/' + id + '/mqdefault.jpg';
      thumbPlaceholder.replaceWith(thumbImg);
    });

    render();
  }

  function initAll(root) {
    (root || document).querySelectorAll('[data-custom-video-slider]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => initAll(e.target));
})();
